import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { App, Button, Space, Typography } from 'antd';
import { useNavigate } from 'react-router';
import { mutations, queries } from '../../../graphql/graphql';
import MapList, { MapListNextPayload } from '../../Map/MapList';
import { PlanView } from '../../Plans/View';
import { Loader } from '../../../components/UI/Loader';

const { Title, Paragraph, Text } = Typography;

type Step = 'map' | 'plan';

export const ContractorProposalCreate: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('map');
  const [selection, setSelection] = useState<MapListNextPayload | null>(null);

  const { data: contractorData, loading: contractorLoading } = useQuery(
    queries.contractors.MY_CONTRACTOR,
    { fetchPolicy: 'network-only' }
  );
  const contractorId = contractorData?.myContractor?.id;

  const [proposalCreate, { loading: creating }] = useMutation(mutations.proposal.PROPOSAL_CREATE);

  const handleMapNext = useCallback(
    async (data?: MapListNextPayload) => {
      if (!data?.planId || !data.mapResultId) {
        message.error('Please select a plan and complete the map step.');
        return;
      }
      setSelection(data);
      setStep('plan');
    },
    [message]
  );

  const handleCreate = useCallback(async () => {
    if (creating) {
      return;
    }
    if (contractorLoading) {
      message.info('Loading contractor profile...');
      return;
    }
    if (!contractorId) {
      message.error('Contractor profile not found.');
      return;
    }
    if (!selection?.planId || !selection.mapResultId) {
      message.error('Plan or map selection is missing.');
      return;
    }

    try {
      const res = await proposalCreate({
        variables: {
          input: {
            contractorId,
            planId: selection.planId,
            mapResultId: selection.mapResultId,
          },
        },
      });
      const proposalId = res.data?.proposalCreate?.id;
      if (!proposalId) {
        message.error('Failed to create proposal.');
        return;
      }
      message.success('Proposal created.');
      navigate(`/contractor/proposal/edit/${proposalId}`);
    } catch (error: any) {
      message.error(error?.message || 'Failed to create proposal.');
    }
  }, [contractorLoading, contractorId, creating, message, navigate, proposalCreate, selection]);

  const header = useMemo(() => {
    if (step === 'plan' && selection?.address) {
      return (
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Address: <Text strong>{selection.address}</Text>
        </Paragraph>
      );
    }
    return (
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Choose a plan and location to start a standalone proposal.
      </Paragraph>
    );
  }, [selection?.address, step]);

  if (step === 'plan' && contractorLoading) {
    return <Loader />;
  }

  return (
    <div style={{ width: '100%' }}>
      {step === 'plan' ? (
        <>
          <Space align="start" style={{ width: '100%' }}>
            <Button style={{ margin: 8 }} onClick={() => setStep('map')}>Back to map</Button>
          </Space>
          <PlanView id={selection?.planId} mapId={selection?.mapResultId} onNext={handleCreate} />
        </>
      ) : (
        <MapList queries={queries.plans.PLANS} onNext={handleMapNext} />
      )}
      {creating && <Loader />}
    </div>
  );
};

export default ContractorProposalCreate;
